<template>
	<div>
		<app-introduction
			v-if="content"
			:title="$t('ContentReview') + ': ' + content.data.keyword.text"></app-introduction>
		<div class="container">
			<el-card>
				<div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px">
					<el-button
						type="primary"
						size="small"
						@click="callChatGPT"
						>{{ $t("callChatGPT") }}</el-button
					>
				</div>
				<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px">
					<el-switch
						v-model="showPromptsPanel"
						:active-text="$t('showPrompts')"
						:inactive-text="$t('hidePrompts')" />
				</div>
				<div
					v-show="showPromptsPanel"
					style="margin-top: 12px">
					<el-card>
						<h4 style="margin-bottom: 8px">{{ $t("systemPrompt") }}</h4>
						<el-input
							type="textarea"
							:rows="6"
							v-model="systemPromptDraft"
							:placeholder="$t('systemPromptPlaceholder')" />
						<h4 style="margin: 12px 0 8px">{{ $t("userPrompt") }}</h4>
						<el-input
							type="textarea"
							:rows="10"
							v-model="userPromptDraft"
							:placeholder="$t('userPromptPlaceholder')" />
					</el-card>
				</div>
				<el-card
					class="editor"
					style="width: 100%; margin-top: 12px">
					<span v-if="reviewResult">
						<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px">
							<html-view-switch @html-view-changed="isShowHtmlView = !isShowHtmlView" />
							<el-button
								type="secondary"
								plain
								size="small"
								@click="copyReviewResult">
								<i class="fas fa-clipboard"> </i> {{ $t("copyReview") }}
							</el-button>
						</div>

						<TiptapEditor
							v-model="reviewResult"
							:is-editable="false"
							:is-public="false"
							:threads-endpoint="null"
							:is-show-html-view="isShowHtmlView">
						</TiptapEditor>
					</span>

					<div v-else>
						<div class="loading">
							<el-icon>
								<Loading />
							</el-icon>
							<span>{{ $t("noReviewResult") }}</span>
						</div>
					</div>
				</el-card>
			</el-card>
		</div>
	</div>
</template>

<script>
import TiptapEditor from "@/components/TiptapV2";
import AppIntroduction from "@/components/AppIntroduction";
import HtmlViewSwitch from "@/components/CampaignManager/HtmlViewSwitch";
import { systemPrompt, userPrompt as userPromptTemplate } from "./contentValidatiorPrompt.js";

export default {
	components: {
		TiptapEditor,
		AppIntroduction,
		HtmlViewSwitch
	},
	props: {
		studyId: String,
		contentId: String
	},
	data() {
		return {
			content: null,
			brief: null,
			briefText: null,
			contentText: null,
			// Effective prompts sent to backend (computed at send time from drafts)
			userPrompt: "",
			systemPrompt: "",
			// Editable drafts shown to user
			showPromptsPanel: false,
			userPromptDraft: userPromptTemplate,
			systemPromptDraft: systemPrompt,
			reviewResult: null,
			isShowHtmlView: false
		};
	},

	methods: {
		async fetchContent() {
			if (this.contentId) {
				const response = await this.$apiV2["get"](
					"https://" + process.env.API_V2_HOST + "/studies/" + this.studyId + "/contents/" + this.contentId
				);
				this.content = response.data;
				this.contentText = response.data.data.text.text;
			}
		},
		async fetchBrief() {
			if (this.contentId) {
				const response = await this.$apiV2["get"]("https://" + process.env.API_V2_HOST + "/studies/" + this.studyId + "/contents/" + this.contentId + "/brief");
				this.brief = response.data;
				this.briefText = response.data.data.text;
			}
		},
		async callChatGPT() {
			try {
				// Build prompts from editable drafts
				this.briefText = typeof this.briefText === "string" ? this.briefText : JSON.stringify(this.briefText, null, 2);
				const effectiveUserPrompt = (this.userPromptDraft || "").replace("{contentText}", this.contentText || "").replace("{briefText}", this.briefText || "");
				const effectiveSystemPrompt = this.systemPromptDraft || "";
				this.userPrompt = effectiveUserPrompt;
				this.systemPrompt = effectiveSystemPrompt;

				// Prepare streaming UI
				this.reviewResult = "";

				const response = await fetch(`https://${process.env.API_HOST}/studies/${this.studyId}/chat-bot`, {
					method: "POST",
					headers: {
						"Content-Type": "application/vnd.api+json",
						Authorization: "Bearer " + JSON.parse(sessionStorage.acccessTokenResponse).access_token
					},
					body: JSON.stringify({
						data: {
							type: "CHAT_BOT",
							attributes: {
								user_input: effectiveUserPrompt,
								system_prompt: effectiveSystemPrompt,
								model: "gpt-4o-mini"
							}
						}
					})
				});

				if (!response.body) {
					throw new Error("Response body is not a ReadableStream.");
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder("utf-8");
				let buffer = "";
				let continueStreaming = true;

				while (continueStreaming) {
					const { done, value } = await reader.read();
					if (done) {
						continueStreaming = false;
					}

					buffer += decoder.decode(value || new Uint8Array(), { stream: true });

					let lastNewlineIndex = buffer.lastIndexOf("\n\n");
					while (lastNewlineIndex !== -1) {
						const eventString = buffer.substring(0, lastNewlineIndex);
						buffer = buffer.substring(lastNewlineIndex + 2);
						const lines = eventString.split("\n");
						let data = "";
						for (const line of lines) {
							data += line;
						}

						if (data === "[DONE]") {
							reader.cancel();
							break;
						}

						// Append chunk (preserve newlines for Tiptap by using \n)
						this.reviewResult = (this.reviewResult || "") + data;

						lastNewlineIndex = buffer.lastIndexOf("\n\n");
					}
				}
			} catch (error) {
				console.error("Error fetching stream:", error);
			}
		},
		async copyReviewResult() {
			try {
				const text = this.reviewResult || "";
				await navigator.clipboard.writeText(text);
				this.$message({
					message: this.$t("reviewCopied"),
					type: "success",
					duration: 2000
				});
			} catch (error) {
				console.error(error);
				this.$message({
					message: this.$t("reviewCopyFailed"),
					type: "error",
					duration: 2000
				});
			}
		},

		openBriefPage() {
			window.open(window.location.origin + "/a/" + this.studyId + "/contents/" + this.contentId + "/brief");
		},
		openReviewPage() {
			window.open(window.location.origin + "/a/" + this.studyId + "/contents/" + this.contentId + "/review");
		}
	},
	async mounted() {
		await this.fetchContent();
		await this.fetchBrief();
	}
};
</script>

<style lang="scss" scoped>
.title {
	display: flex;
	flex-direction: row;
	font-size: 1.5rem;
	margin-bottom: 1rem;
}
</style>
<i18n>
{
  "en": {
    "Content": "Content",
    "ContentReview": "AI Content Review",
    "chatGPTReview": "ChatGPT Review",
    "callChatGPT": "Call ChatGPT",
    "hidePrompts": "Hide prompts",
    "showPrompts": "Show prompts",
    "copyReview": "Copy Review",
    "systemPrompt": "System prompt",
    "userPrompt": "User prompt",
    "systemPromptPlaceholder": "System prompt",
    "userPromptPlaceholder": "User prompt (supports {contentText} and {briefText})",
    "reviewCopied": "Review copied to clipboard",
    "reviewCopyFailed": "Failed to copy review",
    "noReviewResult": "No review generated yet. Click \"Call ChatGPT\" to generate a review.",
    "textRedaction": "Text redaction",
    "briefText": "Content text",
    "validate": "Validate the brief",
    "textSaved": "Text has been saved",
    "contentUpdated": "Content updated",
    "openBrief": "See the brief",
    "openAsPage": "Open as page"
  },
  "fr": {
    "Content": "Contenu",
    "ContentReview": "Revue de contenu par l'IA",
    "chatGPTReview": "Revue ChatGPT",
    "callChatGPT": "Appeler ChatGPT",
    "hidePrompts": "Masquer les prompts",
    "showPrompts": "Afficher les prompts",
    "copyReview": "Copier la revue",
    "systemPrompt": "Prompt système",
    "userPrompt": "Prompt utilisateur",
    "systemPromptPlaceholder": "Prompt système",
    "userPromptPlaceholder": "Prompt utilisateur (supporte {contentText} et {briefText})",
    "reviewCopied": "Revue copiée dans le presse-papiers",
    "reviewCopyFailed": "Échec de la copie de la revue",
    "noReviewResult": "Aucune revue générée pour le moment. Cliquez sur \"Appeler ChatGPT\" pour générer une revue.",
    "textRedaction": "Rédaction de contenu",
    "briefText": "Texte du contenu",
    "validate": "Valider le brief",
    "textSaved": "Texte sauvegardé",
    "contentUpdated": "Contenu mis à jour",
    "openBrief": "Ouvrir le brief",
    "openAsPage": "Ouvrir dans un onglet"
  },
  "de": {
    "Content": "Inhalt",
    "ContentReview": "KI-Inhaltsüberprüfung",
    "chatGPTReview": "ChatGPT-Überprüfung",
    "callChatGPT": "ChatGPT aufrufen",
    "hidePrompts": "Prompts ausblenden",
    "showPrompts": "Prompts anzeigen",
    "copyReview": "Überprüfung kopieren",
    "systemPrompt": "System-Prompt",
    "userPrompt": "Benutzer-Prompt",
    "systemPromptPlaceholder": "System-Prompt",
    "userPromptPlaceholder": "Benutzer-Prompt (unterstützt {contentText} und {briefText})",
    "reviewCopied": "Überprüfung in die Zwischenablage kopiert",
    "reviewCopyFailed": "Fehler beim Kopieren der Überprüfung",
    "noReviewResult": "Noch keine Überprüfung generiert. Klicken Sie auf \"ChatGPT aufrufen\", um eine Überprüfung zu generieren.",
    "textRedaction": "Inhalt schreiben",
    "briefText": "Inhaltstext",
    "validate": "Bestätigen Sie den Auftrag",
    "textSaved": "Gespeicherter Text",
    "contentUpdated": "Inhalt aktualisiert",
    "openBrief": "Brief öffnen",
    "openAsPage": "In einem Tab öffnen"
  }
}
</i18n>
